import React, { useEffect, useState, useCallback } from 'react';
import FalconComponentCard from 'components/common/FalconComponentCard';
import { Button, Col, Form, Row } from 'react-bootstrap';
import ClaimPollsFilter from './components/ClaimPollsFilter';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { handlePreauthFormData, handleResetAll } from 'features/preauthorization/config/preauthorizationSlice';
import { handleResetCreateClaim } from 'features/cliams/config/claimSlice';
import { handleResetPatientSearch } from 'features/eliglibility/eliglibility-check/config/eligibilityCheckSlice';
import { GetAllClaimPolls } from './config/ClaimPollsApi';
import { handleResetFilter, handleClaimPollsFilter } from './config/ClaimPollsSlice';
import { toast } from 'react-toastify';
import ClaimPollsTable from './components/ClaimPollsTable';
import { FiDownloadCloud } from 'react-icons/fi';
import { handleDownloadXLSX } from 'helpers/excel';
import moment from 'moment';

const ClaimPolls = () => {
    const dispatch = useDispatch();
    const { claimPollsFilter, claimPollsData } = useSelector((state) => state.claimPolls);;
    const {
        control,
        watch,
        setValue,
        handleSubmit,
        reset,
        clearErrors,
        formState: { errors, isSubmitted }
    } = useForm({
        defaultValues: {
            branch_id: null,
            polls_type: null,
            start_date: '',
            end_date: '',
        }
    });

    const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    useEffect(() => {
        dispatch(handleResetAll({ check: "reset" }));
        dispatch(handleResetCreateClaim());
        dispatch(handlePreauthFormData({ check: "reset" }));
        dispatch(handleResetPatientSearch());
    }, [dispatch]);

    const requestBody = useCallback((customPage = page, customLimit = rowsPerPage) => ({
        branch_id:
            claimPollsFilter?.branch_id?.branch_id ||
            selectedBranch?.branch_id ||
            selectedBranch?.value ||
            "",
        polls_type: claimPollsFilter?.polls_type?.value || "",
        frm_dte: claimPollsFilter?.start_date || "",
        to_dte: claimPollsFilter?.end_date || "",
        start_point: customPage,
        limit: customLimit,
    }), [claimPollsFilter, selectedBranch, page, rowsPerPage]);

    const fetchClaimPollsList = (body = {}) => {
        try {
            dispatch(GetAllClaimPolls(body)).unwrap().then((data) => {
                if (data?.success) {
                    setTotalRows(data?.total_rows)
                }
            });

        } catch (error) {
            toast.error("Failed to fetch claim polls");
        }
    }

    const handleCheckListFilter = () => {
        setPage(0);
        fetchClaimPollsList(requestBody(0, rowsPerPage));
    };

    const handleReset = () => {
        const defaultBranch = selectedBranch
            ? {
                branch_name: selectedBranch.label || selectedBranch.branch_name,
                branch_id: selectedBranch.value || selectedBranch.branch_id,
                branch_cchino: selectedBranch.cchi || selectedBranch.branch_cchino,
            }
            : null;

        reset({
            branch_id: defaultBranch,
            polls_type: null,
            start_date: '',
            end_date: '',
        });
        dispatch(handleResetFilter());
        
        if (defaultBranch) {
            dispatch(handleClaimPollsFilter({ check: "branch_id", value: defaultBranch }));
        }

        setPage(0);
        setRowsPerPage(10);
        const resetBody = {
            branch_id: defaultBranch?.branch_id || null,
            polls_type: null,
            start_date: '',
            end_date: '',
            start_point: 0,
            limit: 10,
        }
        fetchClaimPollsList(resetBody);
    };

    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
    }, []);

    const handlePerRowsChange = useCallback((newPerPage) => {
        setRowsPerPage(newPerPage);
        setPage(0);
    }, []);

    useEffect(() => {
        const body = requestBody(page, rowsPerPage);
        fetchClaimPollsList(body);
    }, [page, rowsPerPage]);


    const handleDownloadExcel = async () => {
        const excelBody = requestBody(0, 100000);
        try {
            const data = await dispatch(GetAllClaimPolls(excelBody)).unwrap();
            const pollsData = data?.data;
            const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];

            const flattenedArray = pollsData?.map((row, index) => [
                index + 1,
                allBranches?.find((branch) => branch?.branch_id === row?.branch_id)?.branch_name || row?.branch_id,
                row?.created_at ? moment(row?.created_at).format("DD-MM-YYYY hh:mm:ss A") : "",
                row?.polls_id,
                row?.polls_type,
                row?.polls_descp,
                row?.totalpolls_resp,
                row?.totalpolls_req
            ]);

            handleDownloadXLSX(
                [
                    [
                        "#",
                        "Branch Name",
                        "Create Date",
                        "Polls ID",
                        "Polls Type",
                        "Polls Desc.",
                        "Total Polls Response",
                        "Total Polls Request"
                    ],
                    ...(flattenedArray || []),
                ],
                `Claim_Polls_Export_${moment().format("DD-MM-YYYY_hh_mm_ss")}`
            );
        } catch (error) {
            toast.error("Failed to download excel");
        }
    }

    return (
        <Form
            noValidate
            onSubmit={handleSubmit(handleCheckListFilter)}
            className="p-0"
        >
            <FalconComponentCard className="shadow-md" style={{ position: "relative" }}>
                <FalconComponentCard.Header className="d-flex fixed-header justify-content-between align-items-center">
                    <Col xs="auto m-0">
                        <h5 className="text-primary mb-0 fs-10">Claim Polls</h5>
                    </Col>
                    <Col xs="auto" className="d-flex gap-2">
                        <Button size="sm" variant="outline-danger" onClick={handleReset}>
                            Reset
                        </Button>
                        <Button type="submit" size="sm" variant="success">
                            Filter
                        </Button>
                        <Button size="sm" variant="warning" onClick={handleDownloadExcel}>
                            <FiDownloadCloud size={16} />
                            Excel
                        </Button>
                    </Col>
                </FalconComponentCard.Header>
                <FalconComponentCard.Body style={{ marginTop: "40px" }}>
                    <ClaimPollsFilter
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        errors={errors}
                        isSubmitted={isSubmitted}
                    />
                    <div className="mt-4">
                        <ClaimPollsTable
                            data={claimPollsData || []}
                            totalRows={totalRows}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            handlePageChange={handlePageChange}
                            handlePerRowsChange={handlePerRowsChange}
                        />
                    </div>
                </FalconComponentCard.Body>
            </FalconComponentCard>
        </Form>
    );
};

export default ClaimPolls;
